import type { NextPage } from "next";
import React, { useState } from "react";
import GeneratorIframe from "@/client/lib/token-renderer/GeneratorIframe";
import {
  useTokenRenderer,
  RenderSettings,
} from "@/client/lib/token-renderer/useTokenRenderer";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { Asset } from "@/shared/types/asset";
import Button, {
  ButtonLoading,
} from "@/client/components/frame-design-system/buttons/Button";
import Input from "@/client/components/frame-design-system/inputs/Input";
import { Lock } from "@/client/components/icons/lock";
import { Information } from "@/client/components/icons/information";
import Alert from "@/client/components/frame-design-system/alerts/Alert";
import ReactTooltip from "react-tooltip";
import { getFlagshipTokenUrl } from "@/client/lib/links";

export type Props = {
  asset: Asset;
  renderDelay: number;
};

const calculateWidth = (height: number, aspectRatio: number) => {
  if (height === 0 || aspectRatio === 0 || !height || !aspectRatio) {
    return 0;
  }
  return Math.round(height * aspectRatio);
};

const Render: NextPage<Props> = ({ asset, renderDelay }: Props) => {
  const { generatorIframeRef, startRendering, error, message, isLoading } =
    useTokenRenderer();
  const [isPNG, setIsPNG] = useState(true);
  const [width, setWidth] = useState<number>(
    calculateWidth(800, asset.aspectRatio || 1)
  );

  const [formValues, setFormValues] = useState({
    fileType: "png",
    fps: 24,
    renderDelay: renderDelay,
    duration: 3,
    height: 800,
    width: 0,
  });

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleRenderValues = (event: any) => {
    event.preventDefault();

    if (generatorIframeRef.current) {
      if (formValues.height <= 0 || width <= 0) {
        console.error("Invalid height or width.");
        return;
      }

      const renderValues: RenderSettings = {
        contractAddress: asset.contractAddress,
        tokenId: asset.tokenId,
        fileType: formValues.fileType,
        fps: formValues.fps,
        renderDelay: formValues.renderDelay,
        duration: formValues.duration * 1000,
        height: formValues.height,
        width: width,
      };

      try {
        startRendering(renderValues);
      } catch (err) {
        console.error("Rendering error:", err);
      }
    }
  };

  if (error) {
    console.error("Renderer error: ", error);
  }

  const RendererForm = () => {
    return (
      <form className="w-[322px] p-6 bg-white dark:bg-neutral-800 rounded-2xl border-[1.5px] dark:border-neutral-700">
        <h2 className="mb-4 text-lg font-medium text-neutral-800 dark:text-white">
          Settings
        </h2>
        <div className="flex items-center justify-between py-3 space-y-1">
          <label
            htmlFor="fileType"
            className="block font-medium text-md text-neutral-800 dark:text-white"
          >
            Filetype
          </label>
          <select
            id="fileType"
            name="fileType"
            onChange={(e) => {
              setIsPNG(e.target.value === "png");
              handleInputChange(e);
            }}
            defaultValue={formValues.fileType}
            className="block p-1 mt-1 font-medium text-center border border-gray-300 rounded-lg shadow-sm bg-neutral-200 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500 dark:border-neutral-700 focus:outline-none focus:ring-neutral-500 focus:border-neutral-500 sm:text-sm"
          >
            <option value="png">PNG</option>
            <option value="gif">GIF</option>
            <option value="mp4">MP4</option>
          </select>
        </div>
        <div className="flex items-center justify-between py-3 space-y-1">
          <label
            htmlFor="fps"
            className="block font-medium text-md dark:text-white text-neutral-800"
          >
            Framerate
          </label>
          <select
            id="fps"
            name="fps"
            onChange={(e) => handleInputChange(e)}
            disabled={isPNG}
            defaultValue={formValues.fps}
            className="block p-1 mt-1 font-medium text-center border border-gray-300 rounded-lg shadow-sm bg-neutral-200 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500 dark:border-neutral-700 focus:outline-none focus:ring-neutral-500 focus:border-neutral-500 sm:text-sm"
          >
            <option value="24">24</option>
            <option value="30">30</option>
            <option value="60">60</option>
          </select>
        </div>
        <div className="flex items-center justify-between py-3 space-y-1">
          <div className="flex items-center space-x-1">
            <label
              htmlFor="renderDelay"
              className="block font-medium text-md dark:text-white text-neutral-800"
            >
              Render Delay (ms)
            </label>
            <>
              <ReactTooltip
                id="render-delay-tooltip"
                backgroundColor="black"
                className=""
                effect="solid"
                place="top"
              />
              <Information
                data-for="render-delay-tooltip"
                data-tip="The number of milliseconds to wait before rendering a piece (useful for animated pieces)"
                className="dark:text-white text-darkside-800"
              />
            </>
          </div>
          <Input
            id="renderDelay"
            name="renderDelay"
            onChange={(e) => handleInputChange(e)}
            defaultValue={formValues.renderDelay}
            className="w-24 h-10"
          />
        </div>
        <div className="flex items-center justify-between py-3 space-y-1">
          <div className="flex space-x-1">
            <label
              htmlFor="duration"
              className="block font-medium text-md dark:text-white text-neutral-800"
            >
              Duration (s)
            </label>
            <>
              <ReactTooltip
                id="duration-tooltip"
                backgroundColor="black"
                className=""
                effect="solid"
                place="top"
              />
              <Information
                data-for="duration-tooltip"
                data-tip="The number of seconds you want your render to be"
                className="dark:text-white text-darkside-800"
              />
            </>
          </div>
          <Input
            id="duration"
            name="duration"
            onChange={(e) => handleInputChange(e)}
            placeholder="3"
            defaultValue="3"
            disabled={isPNG}
            className="w-24 h-10"
          />
        </div>
        <div className="w-full my-2 border-t border-gray-300 dark:border-neutral-600"></div>

        <div className="flex items-center justify-between pt-4 pb-1 space-y-1">
          <label
            htmlFor="height"
            className="block font-medium text-md dark:text-white text-neutral-800"
          >
            Height (px)
          </label>
          <Input
            id="height"
            name="height"
            placeholder="0"
            defaultValue="800"
            onChange={(e) => {
              setWidth(
                calculateWidth(parseInt(e.target.value), asset.aspectRatio || 1)
              );
              handleInputChange(e);
            }}
            className="w-24 h-10"
          />
        </div>
        <div className="flex items-center justify-between pt-1 pb-4 space-y-1">
          <label
            htmlFor="width"
            className="block font-medium text-md text-neutral-800 dark:text-white"
          >
            Width (px)
          </label>
          <div className="flex items-center justify-end">
            <Lock size={24} className="dark:text-white text-darkside-800" />
            <Input
              id="width"
              name="width"
              disabled={true}
              onChange={handleInputChange}
              value={width}
              className="w-24 h-10 ml-1"
            />
          </div>
        </div>
        <Button
          type="submit"
          onClick={handleRenderValues}
          size="large"
          title={isLoading ? "Saving..." : "Save"}
          className="w-full"
        >
          {isLoading ? <ButtonLoading /> : "Save"}
        </Button>
        {message ? (
          <div className="mt-4">
            <Alert type="info" title="Loading..." subtitle={message} />
          </div>
        ) : null}
      </form>
    );
  };

  return (
    <div className="relative">
      <div className="z-50 flex flex-col w-full">
        <div className="h-[80px] flex items-center border-b-2 dark:border-neutral-700">
          <a href={getFlagshipTokenUrl(asset.contractAddress, asset.tokenId)}>
            <div className="w-11 h-11 mx-[26px] bg-neutral-100 hover:bg-neutral-200 hover:cursor-pointer dark:bg-neutral-700 dark:hover:bg-neutral-600 transition flex items-center justify-center rounded-full">
              <ArrowLeftIcon className="w-5 h-5 text-black dark:text-white" />
            </div>
          </a>

          <div className="flex flex-col">
            <h2 className="font-medium dark:text-neutral-200">
              Artwork Renderer
            </h2>
            <h3 className="text-neutral-600 dark:text-neutral-500">
              {asset.name}
            </h3>
          </div>
        </div>
        <div className="flex justify-between md:h-[calc(100vh_-_80px)] p-8 w-full h-full bg-neutral-100 dark:bg-neutral-900">
          <div>
            <div>{RendererForm()}</div>
          </div>
          <div className="flex justify-center flex-grow w-full">
            <iframe
              className={`w-4/5`}
              src={asset?.generatorUrl || asset?.animationUrl}
            ></iframe>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 opacity-0 -z-50">
        <GeneratorIframe ref={generatorIframeRef} />
      </div>
    </div>
  );
};

export default Render;
